/**
 * A proposal is data until a scientist confirms it.
 *
 * Nothing here calls the server. These functions turn a proposal into the words
 * a scientist reads before deciding, and into the arguments of the *existing*
 * curation call they are agreeing to make. Confirming is the browser calling
 * `api.exclude` or `api.include` under the user's own name, with
 * `source: "copilot-proposed"` so the audit trail records how the decision was
 * reached rather than who made it -- which is the scientist, either way.
 *
 * See ADR-0011.
 */

export const COPILOT_SOURCE = "copilot-proposed";

const KINDS = ["exclude", "reinclude", "acknowledge_checks"];

export function isApplicable(proposal) {
  if (!proposal || proposal.committed) return false;
  if (!KINDS.includes(proposal.kind)) return false;
  if (proposal.kind === "acknowledge_checks") return true;
  return Boolean(proposal.point_ids?.length) && (proposal.kind !== "exclude" || Boolean(proposal.reason));
}

/** The sentence on the confirm card. */
export function describe(proposal) {
  const n = proposal.point_ids?.length || 0;
  const points = `${n} point${n === 1 ? "" : "s"}`;
  switch (proposal.kind) {
    case "exclude":
      return `Mark ${points} as an outlier — ${proposal.reason}`;
    case "reinclude":
      return `Put ${points} back into the fit`;
    case "acknowledge_checks":
      return `Record that you have read the ${proposal.n_checks} quality-control check${
        proposal.n_checks === 1 ? "" : "s"
      }. It does not clear them.`;
    default:
      return "Unrecognised proposal.";
  }
}

/**
 * What confirming would cost, in the scientist's own terms, or `null` when it
 * costs nothing beyond the sentence above.
 *
 * Reinclusion is the case that needs this. Putting a point back deletes the
 * exclusion that held it out -- the reason, the name and the date go with it --
 * and re-fits every curve the point belongs to. The reversal is recorded, but
 * the original decision is no longer the current one, and nobody should agree
 * to that from a sentence that only says "put 3 points back".
 */
export function consequence(proposal) {
  if (!isApplicable(proposal) || proposal.kind !== "reinclude") return null;
  const reasons = (proposal.discards || [])
    .map((d) => d.reason)
    .filter(Boolean);
  const unique = [...new Set(reasons)];
  return {
    warning:
      "This re-fits every affected curve and discards the reason each point was excluded for.",
    reasons: unique,
  };
}

/**
 * The call a confirmation turns into: which existing route, with what body.
 *
 * Returned rather than made, so what confirming does can be asserted without a
 * server -- including that it carries the user's name and never a model's.
 */
export function toRequest(proposal, user) {
  if (!isApplicable(proposal)) return null;
  switch (proposal.kind) {
    case "exclude":
      return {
        action: "exclude",
        observationIds: proposal.point_ids,
        reason: proposal.reason,
        excludedBy: user || null,
        source: COPILOT_SOURCE,
      };
    case "reinclude":
      // Named, like an exclusion is. Reversing a decision is a decision.
      return {
        action: "include",
        observationIds: proposal.point_ids,
        reinstatedBy: user || null,
        source: COPILOT_SOURCE,
      };
    case "acknowledge_checks":
      return { action: "acknowledge", acknowledgedBy: user || null };
    default:
      return null;
  }
}
